import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCurrentInternalUser, notifyCurrentUserChanged } from '../lib/currentUser';
import { ChevronsUpDown } from 'lucide-react';
import AvatarPicker, { Avatar } from './AvatarPicker';

// Shared sidebar footer rendered on every internal page — surfaces the
// signed-in user identity and the account-level actions. Brand
// refresh: the previous initials-on-green-circle was replaced with
// the <Avatar /> component, reading the chosen avatar id from
// internal_users.avatar_id (falling back to the 'signature' default
// when the user hasn't picked one yet). Clicking the card opens a
// popover above it with Change Avatar / Change Password / Sign Out;
// outside-click and Escape close it. Sign Out lives inside the menu
// so the destructive action takes one deliberate extra click instead
// of being a misclick risk.

const ROLE_LABELS = {
  admin: 'Admin',
  super_user: 'Super User',
  finance: 'Finance',
  staff: 'Team',
};
const formatRole = (role) => {
  if (!role) return 'Team';
  return ROLE_LABELS[role] || role.charAt(0).toUpperCase() + role.slice(1);
};

// 1–2 character initials from full_name → "AB" / "RE", first letter
// of email, or 'R' as a final fallback so the avatar always has
// something to render.
const getInitials = (user) => {
  if (user?.full_name) {
    const parts = user.full_name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  }
  if (user?.email) return user.email[0].toUpperCase();
  return 'R';
};

export default function SidebarUserMenu() {
  const navigate = useNavigate();
  const { user } = useCurrentInternalUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Local optimistic overrides so the sidebar updates immediately on
  // save without waiting for the hook to refetch. Null means "use
  // whatever's on the user record". avatarUrlOverride is a tri-state:
  //   undefined → no override, use user.avatar_url
  //   string    → use this url (just-uploaded photo)
  //   null      → user explicitly cleared their uploaded photo
  const [avatarOverride, setAvatarOverride] = useState(null);
  const [avatarUrlOverride, setAvatarUrlOverride] = useState(undefined);
  const [avatarColourOverride, setAvatarColourOverride] = useState(undefined);
  // Preferences modal — currently hosts a single seasonal-accessories
  // toggle; designed to be a hub for future per-user prefs without
  // having to add new sidebar items each time. Optimistic override
  // pattern mirrors the avatar's so the toggle flips immediately on
  // click without waiting for the DB round-trip.
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [seasonalOverride, setSeasonalOverride] = useState(undefined);
  const [ottoMotionOverride, setOttoMotionOverride] = useState(undefined);
  // User-guide slide-out (mirrors the supplier portal's in-menu Help).
  const wrapperRef = useRef(null);

  // Close the popover on outside click or Escape so it doesn't trap
  // input — standard menu-affordance expectations.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Lock body scroll while the avatar picker modal is open and let
  // Escape close it.
  useEffect(() => {
    if (!pickerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setPickerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  // Same lifecycle for the Preferences modal.
  useEffect(() => {
    if (!preferencesOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setPreferencesOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [preferencesOpen]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await supabase.auth.signOut();
    navigate('/');
  };
  const handleChangePassword = () => {
    setMenuOpen(false);
    navigate('/change-password');
  };
  const handleChangeAvatar = () => {
    setMenuOpen(false);
    setPickerOpen(true);
  };
  const handleOpenPreferences = () => {
    setMenuOpen(false);
    setPreferencesOpen(true);
  };
  // Goes to the HANDBOOK route rather than opening the old help modal. The
  // handbook is the one long-form explainer and it is a real page, so it can be
  // linked to, bookmarked and sent — which a modal never could. It is also what
  // the internal welcome email points at, so the menu now agrees with the email.
  const handleOpenGuide = () => {
    setMenuOpen(false);
    navigate('/handbook');
  };
  // Same shape as handleToggleSeasonal below: optimistic flip, roll back on
  // error, then notify so every live hook re-pulls. The notify is what makes
  // this one take effect immediately -- OttoMotionPreference listens on the
  // same channel and restamps the root attribute, so Otto stops mid-page
  // rather than on next reload.
  const handleToggleOttoMotion = async (nextValue) => {
    if (!user?.id) return;
    setOttoMotionOverride(nextValue);
    const { error } = await supabase
      .from('internal_users')
      .update({ otto_motion_enabled: nextValue })
      .eq('id', user.id);
    if (error) {
      setOttoMotionOverride(undefined);
      return;
    }
    notifyCurrentUserChanged();
  };

  const handleToggleSeasonal = async (nextValue) => {
    if (!user?.id) return;
    // Optimistic: flip immediately so the toggle feels instant + the
    // mascot updates straight away. Rolls back below on DB error.
    setSeasonalOverride(nextValue);
    const { error } = await supabase
      .from('internal_users')
      .update({ seasonal_accessories_enabled: nextValue })
      .eq('id', user.id);
    if (error) {
      setSeasonalOverride(undefined);
      return;
    }
    notifyCurrentUserChanged();
  };
  const handleSaveAvatar = async (avatarId, avatarUrl, avatarColour) => {
    if (!user?.id) { setPickerOpen(false); return; }
    setAvatarOverride(avatarId);
    setAvatarUrlOverride(avatarUrl || null);
    setAvatarColourOverride(avatarColour || null);
    setPickerOpen(false);
    const { error } = await supabase
      .from('internal_users')
      .update({ avatar_id: avatarId, avatar_url: avatarUrl || null, avatar_colour: avatarColour || null })
      .eq('id', user.id);
    if (error) {
      // Roll back the optimistic overrides if the write fails so the
      // sidebar doesn't lie about what's persisted.
      setAvatarOverride(null);
      setAvatarUrlOverride(undefined);
      setAvatarColourOverride(undefined);
      // ...and SAY so. Failing silently here just looks like "the avatar
      // doesn't stick" — which is exactly how a missing avatar_colour column
      // (PGRST204, migration not run yet) presents.
      window.alert(
        `Couldn't save your avatar: ${error.message}${error.code ? ` (${error.code})` : ''}` +
        (/avatar_colour/i.test(error.message || '')
          ? '\n\nThe avatar_colour column is missing — run db/migrations/2026-07-internal-users-avatar-colour.sql in Supabase.'
          : '')
      );
      return;
    }
    // Tell every other live useCurrentInternalUser instance on the
    // page to refetch - the dashboard welcome card etc. read from
    // the hook directly and would otherwise keep showing the
    // previous avatar until the next navigation.
    notifyCurrentUserChanged();
  };

  const displayName = user?.full_name || user?.email || 'Signed in';
  const firstName = user?.full_name?.split(' ')[0] || displayName;
  const initials = getInitials(user);
  const metaLabel = (user?.job_title && user.job_title.trim()) || formatRole(user?.role);
  const effectiveAvatarId = avatarOverride || user?.avatar_id || 'initials';
  const effectiveAvatarUrl = avatarUrlOverride !== undefined ? avatarUrlOverride : (user?.avatar_url || null);
  // NULL colour = never chosen (e.g. anyone who existed before avatar_colour
  // landed); the Avatar falls back to the icon's own default pairing.
  const effectiveAvatarColour = avatarColourOverride !== undefined ? avatarColourOverride : (user?.avatar_colour || null);

  return (
    <div ref={wrapperRef} className="p-2 xl:p-4 border-t border-white/[0.06] relative">
      {/* UserChip (Part A5) — shared across every Fusion product: a
          bordered chip with a lime-ring avatar, name + role, and a
          trailing expand caret. Identical component/shape to
          Quotezilla's. The mascot lives inside the avatar (A5), never
          the header. */}
      <button
        onClick={() => setMenuOpen(o => !o)}
        title={displayName}
        aria-expanded={menuOpen}
        aria-haspopup="true"
        className="w-full flex items-center justify-center xl:justify-start gap-0 xl:gap-3 bg-sb-hover rounded-xl border border-sb-line hover:border-sb-guide transition-colors p-2 xl:p-3 text-left">
        <span className="flex-shrink-0 flex items-center justify-center rounded-full ring-2 ring-fusion-green/70" aria-hidden="true">
          <Avatar id={effectiveAvatarId} colour={effectiveAvatarColour} url={effectiveAvatarUrl} initials={initials} size={36} />
        </span>
        <span className="hidden xl:block min-w-0 flex-1">
          <span className="block font-['Metropolis'] text-sm font-semibold text-[#E6E8E1] truncate">
            {firstName}
          </span>
          <span className="block font-['Metropolis'] text-xs text-[#8A9081] truncate" title={metaLabel}>
            {metaLabel}
          </span>
        </span>
        <ChevronsUpDown size={16} strokeWidth={2} aria-hidden="true" className="hidden xl:block flex-shrink-0 text-sb-muted" />
      </button>
      {menuOpen && (
        <div
          role="menu"
          className="absolute left-2 xl:left-4 right-2 xl:right-4 bottom-full mb-2 w-[240px] xl:w-auto bg-[#1c1d1a] border border-[#2A2C27] rounded-xl shadow-lg overflow-hidden z-50">
          <button
            type="button"
            role="menuitem"
            onClick={handleChangeAvatar}
            className="w-full text-left px-4 py-3 font-['Metropolis'] text-sm text-[#F9FCF1] hover:bg-active-green/10 hover:text-active-green transition-colors flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21a8 8 0 0 1 16 0" />
            </svg>
            Change Avatar
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleChangePassword}
            className="w-full text-left px-4 py-3 font-['Metropolis'] text-sm text-[#F9FCF1] hover:bg-active-green/10 hover:text-active-green transition-colors flex items-center gap-2 border-t border-[#2A2C27]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Change Password
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleOpenPreferences}
            className="w-full text-left px-4 py-3 font-['Metropolis'] text-sm text-[#F9FCF1] hover:bg-active-green/10 hover:text-active-green transition-colors flex items-center gap-2 border-t border-[#2A2C27]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Preferences
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleOpenGuide}
            className="w-full text-left px-4 py-3 font-['Metropolis'] text-sm text-[#F9FCF1] hover:bg-active-green/10 hover:text-active-green transition-colors flex items-center gap-2 border-t border-[#2A2C27]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            Handbook
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            className="w-full text-left px-4 py-3 font-['Metropolis'] text-sm text-[#DADCCD] hover:bg-[#E0290A]/10 hover:text-[#E0290A] transition-colors flex items-center gap-2 border-t border-[#2A2C27]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign Out
          </button>
        </div>
      )}

      {/* Avatar picker — rendered through a portal into document.body
          so the fixed-position overlay can't be trapped by any
          ancestor stacking context or transformed containing block
          (the sticky sidebar, the welcome banner's relative wrapper,
          etc.). Without the portal the dashboard panel was bleeding
          through the top of the picker. The picker draws its own
          dark backdrop so we don't add another dim layer. */}
      {pickerOpen && createPortal(
        <div>
          {/* The picker is a self-contained pop-out modal now — it renders its
              own dimmed backdrop and dialog, so this wrapper must not add a
              second full-screen surface on top of the page. */}
          <AvatarPicker
            initials={initials}
            initialId={effectiveAvatarId}
            initialColour={effectiveAvatarColour}
            initialUrl={effectiveAvatarUrl}
            currentUserId={user?.id || null}
            userName={firstName}
            userRole={metaLabel}
            onSave={handleSaveAvatar}
            onCancel={() => setPickerOpen(false)} />
        </div>,
        document.body
      )}

      {/* Preferences modal — portal'd onto body for the same stacking
          reasons as the avatar picker. v1 hosts a single seasonal-
          accessories toggle; future per-user prefs land here. */}
      {preferencesOpen && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Preferences"
          onClick={(e) => { if (e.target === e.currentTarget) setPreferencesOpen(false); }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white border border-line rounded-xl w-full max-w-md shadow-lift">
            <div className="flex items-center justify-between px-5 py-4 border-b border-line">
              <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.25em] uppercase text-fusion-forest">Preferences</p>
              <button
                type="button"
                onClick={() => setPreferencesOpen(false)}
                aria-label="Close"
                className="font-['Metropolis'] text-fusion-mid-grey hover:text-ink transition-colors text-lg leading-none">
                ×
              </button>
            </div>
            <div className="px-5 py-5 space-y-5">
              {(() => {
                const ottoMotionEnabled = ottoMotionOverride !== undefined
                  ? ottoMotionOverride
                  : (user?.otto_motion_enabled ?? true);
                return (
                  <label className="flex items-start gap-4 cursor-pointer">
                    <div className="min-w-0 flex-1">
                      <p className="font-['Metropolis'] text-sm font-semibold text-ink">Animate Otto</p>
                      <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mt-1 leading-relaxed">
                        When on, Otto blinks, stretches and occasionally moves across the page. When off, he stays exactly where he is and never moves. He is still there &mdash; this only stops the motion.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={ottoMotionEnabled}
                      onClick={() => handleToggleOttoMotion(!ottoMotionEnabled)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors mt-1 ${ottoMotionEnabled ? 'bg-[#92D400]' : 'bg-[#DADCCD]'}`}>
                      <span
                        aria-hidden="true"
                        className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-all ${ottoMotionEnabled ? 'left-[23px]' : 'left-[3px]'}`} />
                    </button>
                  </label>
                );
              })()}
              {(() => {
                const seasonalEnabled = seasonalOverride !== undefined
                  ? seasonalOverride
                  : (user?.seasonal_accessories_enabled ?? true);
                return (
                  <label className="flex items-start gap-4 cursor-pointer">
                    <div className="min-w-0 flex-1">
                      <p className="font-['Metropolis'] text-sm font-semibold text-ink">Show seasonal accessories</p>
                      <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mt-1 leading-relaxed">
                        When on, Otto wears small accessories around seasonal moments (Santa hat at Christmas, party hat at New Year, etc.). When off, Otto is plain year-round.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={seasonalEnabled}
                      onClick={() => handleToggleSeasonal(!seasonalEnabled)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors mt-1 ${seasonalEnabled ? 'bg-[#92D400]' : 'bg-[#DADCCD]'}`}>
                      <span
                        aria-hidden="true"
                        className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-all ${seasonalEnabled ? 'left-[23px]' : 'left-[3px]'}`} />
                    </button>
                  </label>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* User guide slide-out — the admin equivalent of the supplier
          portal's in-menu Help. Self-portals via SideDrawer. */}
    </div>
  );
}
